import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import intl from 'react-intl-universal';
import { FormItem, Dropdown, Typography } from '@getsynapse/design-system';
import { Option } from 'utils/customTypes';
import {
  selectTimePeriods,
  fetchYearTimePeriods,
} from 'state/YearTimePeriods/YearTimePeriodSlice';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { TimePeriodType as TimePeriodValueType } from 'utils/types/strategyGoals';
import { getInitialValueForDropDown } from 'utils/functions';
import classnames from 'classnames';

type TimePeriodProps = {
  value?: TimePeriodValueType;
  isRequired?: boolean;
  onChange: (value: string, prop: string) => void;
  newGoal?: boolean;
};

export type TimePeriodType = {
  label: string;
  shortLabel: string;
};

const TimePeriod = ({
  value,
  onChange,
  isRequired = true,
  newGoal = false,
}: TimePeriodProps) => {
  const timePeriods = useSelector(selectTimePeriods);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!timePeriods?.length) {
      dispatch(fetchYearTimePeriods());
    }
  }, [dispatch, timePeriods?.length]);

  const timePeriodOptions = useMemo(
    () =>
      timePeriods?.map((timePeriod: TimePeriodType) => ({
        label: timePeriod.label,
        value: timePeriod.shortLabel,
      })),
    [timePeriods]
  );

  const changeHandler = (option: Option) => {
    const timePeriod = option.value;
    if (!newGoal) {
      onChange(timePeriod, 'timePeriodType');
    } else {
      onChange(timePeriod, 'timePeriod');
    }
  };

  const timePeriodValue = useMemo(() => {
    return isRequired
      ? getInitialValueForDropDown(timePeriodOptions, value?.type)
      : `${value?.type} ${value?.year}`;
  }, [value, isRequired, timePeriodOptions]);

  return (
    <FormItem
      className={classnames('w-full', {
        'mb-4': !newGoal,
      })}
      label={intl.get('STRATEGY_GOALS.ADD_GOAL_MODAL.TIME_PERIOD')}
      labelProps={{
        required: isRequired,
        'data-cy': 'time-period_label',
      }}
    >
      {isRequired && (
        <Dropdown
          options={timePeriodOptions}
          values={timePeriodValue as Option[]}
          onChange={changeHandler}
          triggerProps={{
            placeholder: intl.get(
              'STRATEGY_GOALS.ADD_GOAL_MODAL.TIME_PERIOD_PLACEHOLDER'
            ),
            'data-testid': 'time-period_dropdown',
          }}
          listProps={{
            'data-testid': 'time-period_list',
          }}
        />
      )}
      {!isRequired && timePeriodValue && (
        <Typography variant='body' data-cy='time-period_value'>
          {timePeriodValue}
        </Typography>
      )}
    </FormItem>
  );
};

export default TimePeriod;
